/* eslint-disable default-param-last */
import { handle } from 'redux-pack';
import {
	SET_SELECTED_VIDEO,
	SET_SELECTED_AUDIO,
	SET_SELECTED_AUDIO_OUTPUT,
	SET_DISPLAY_NAME,
	SET_CONNECTION_READY,
	SET_MAIN_VIDEO_STREAM,
	SET_SHARING_SCREEN,
	SET_CONNECTED_PRESENTER_VIDEOS,
	SET_CONNECTED_MEDIA_VIDEOS,
	SET_VIDEO_DEVICES,
	SET_AUDIO_DEVICES,
	SET_AUDIO_OUTPUTS,
	SET_SELECTED_RESOLUTION,
	Resolution,
	SET_LIVE,
	UPDATE_LOCAL_ID,
	SET_VIDEO_MUTE,
	SET_AUDIO_MUTE,
	SET_PREVIEW_MODE,
	SET_LIVE_VIDEO_MAP,
	DESTROY_MAIN_VIDEO_STREAM,
	SET_HIDDEN_TITLES,
	SET_USE_MOBILE,
	RESET_AUDIO_MUTED_PREFS,
	SYNC_VIDEO_REDUCER_STATE,
	ADD_PRESENTER_VIDEO,
	REMOVE_PRESENTER_VIDEO,
	SET_IS_INITIAL_DEVICE_LOAD,
	ADD_FEED_WEBRTCID,
	REMOVE_FEED_WEBRTCID,
	CLEAR_FEED_WEBRTCIDS,
	SET_SHOULD_REPUBLISH_VIDEO,
	GET_SRT_STREAMS,
	UPSERT_SRT_STREAM,
	DELETE_SRT_STREAM,
	SET_SELECTED_SRT_STREAM_ID,
	GET_ONSITE_LOCATIONS,
	UPSERT_ONSITE_LOCATION,
	DELETE_ONSITE_LOCATION,
	SET_SELECTED_ONSITE_LOCATION_ID,
	ADD_ONSITE_LOCATION_STREAM,
	SET_ONSITE_LOCATION_STREAMS,
	REMOVE_ONSITE_LOCATION_STREAM,
	SET_ONSITE_LOCATION_SESSION,
	RESET_ONSITE_LOCATION_STREAMS,
	UPSERT_ONSITE_LOCATION_LOCAL,
} from '../actions/video';
import { VideoStream } from '../../components/types/videos';
import { arrayUpsert } from '../../utils/utils';

export enum SrtStreamStatus {
	active = 'active',
	idle = 'idle',
	disabled = 'disabled',
}

export interface SrtStream {
	id: number;
	playbackId: string;
	muxId: string;
	streamKey: string;
	srtPassphrase: string;
	status: SrtStreamStatus;
	thumbnail: string;
	name: string;
	safetyImage: string;
	volume: number;
	muted: boolean;
	videoMuted: boolean;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface OnsiteLocation {
	id: number;
	meeting?: number;
	channel?: number;
	meetingOrder: number;
	locationName: string;
	pathName: string;
	safetyImage: string;
	holdSlide: string;
	cloudConvertId: string | null;
	advancedAudio: boolean;
	resolution1080p: boolean;
	hideSelfView: boolean;
	useSlideDeck: boolean;
	srtId: number | null;
	useSrt: boolean;
	deleted?: boolean;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface PreviousLocation {
	locationName: string;
	safetyImage: string;
	holdSlide: string;
}

export interface OnsiteLocationStream {
	id: string;
	srcObject: MediaStream;
}

export interface OnsiteLocationSession {
	id: number | null;
	route: OnsiteLocationSessionRoute;
}

export enum OnsiteLocationSessionRoute {
	Entry = 'entry',
	Publishing = 'publishing',
	Canvas = 'canvas',
	LimitedInputCanvas = 'limited-input-canvas',
}

export const LOCATION_PUBLISHING_ROUTES = [
	OnsiteLocationSessionRoute.Publishing,
	OnsiteLocationSessionRoute.LimitedInputCanvas,
];

export const LOCATION_CANVAS_ROUTES = [
	OnsiteLocationSessionRoute.Canvas,
	OnsiteLocationSessionRoute.LimitedInputCanvas,
];

export interface VideoState {
	selectedVideo: MediaDeviceInfo | null;
	selectedAudio: MediaDeviceInfo | null;
	selectedOutput: MediaDeviceInfo | null;
	displayName: string;
	connectionReady: boolean;
	liveVideoMap: string[];
	liveVideoUpdated: boolean;
	mainVideoStream: any;
	sharingScreen: boolean;
	connectedPresenterVideos: VideoStream[];
	connectedMediaVideos: VideoStream[];
	videoDevices: MediaDeviceInfo[];
	audioDevices: MediaDeviceInfo[];
	audioOutputs: MediaDeviceInfo[];
	availableResolutions: Resolution[];
	selectedResolution: Resolution;
	isLive: boolean;
	// preference set during onboarding and passed into opentok
	videoMuted: boolean;
	// preference set during onboarding and passed into opentok
	audioMuted: boolean;
	previewMode: boolean; // true if user is in the settings tab while isLive is false
	hiddenTitles: Array<number>;
	useMobile: boolean;
	isInitialDeviceLoad: boolean;
	feedWebRTCIds: string[];
	shouldRepublishVideo: boolean;
	srtStreams: SrtStream[];
	selectedSrtStreamId: number;
	onsiteLocations: OnsiteLocation[]; // db data for onsite locations
	onsiteLocationsLoaded: boolean;
	onsiteLocationStreams: Map<number, OnsiteLocationStream[]>; // opentok streams for each location
	onsiteLocationSession: OnsiteLocationSession | null; // page state for onsite location session
	selectedOnsiteLocationId: number;
}

export interface Bitrate {
	[key: string]: number;
}

const availableResolutions: Resolution[] = [
	{
		label: 'Full HD (1080p)',
		value: {
			height: 1080,
			width: 1920,
		},
	},
	{
		label: 'High Definition (720p)',
		value: {
			height: 720,
			width: 1280,
		},
	},
	{
		label: 'Standard Definition (540p)',
		value: {
			height: 540,
			width: 960,
		},
	},
	{
		label: 'Low Definition (360p)',
		value: {
			height: 360,
			width: 640,
		},
	},
];

export const initialVideoState: VideoState = {
	selectedVideo: null,
	selectedAudio: null,
	selectedOutput: null,
	displayName: '',
	connectionReady: false,
	liveVideoMap: [],
	liveVideoUpdated: false,
	mainVideoStream: null,
	sharingScreen: false,
	connectedPresenterVideos: [],
	connectedMediaVideos: [],
	videoDevices: [],
	audioDevices: [],
	audioOutputs: [],
	availableResolutions,
	selectedResolution: availableResolutions[0],
	isLive: false,
	videoMuted: false,
	audioMuted: false,
	previewMode: false,
	hiddenTitles: [],
	useMobile: false,
	isInitialDeviceLoad: true,
	feedWebRTCIds: [],
	shouldRepublishVideo: true,
	srtStreams: [],
	selectedSrtStreamId: 0,
	onsiteLocations: [],
	onsiteLocationsLoaded: false,
	onsiteLocationStreams: new Map(),
	onsiteLocationSession: null,
	selectedOnsiteLocationId: 0,
};

interface Action {
	type: string,
	payload: any
}

// using a Map here instead of a switch case - switches can get slow
// as you add lots of actions, this has a larger overhead for
// small maps, but a much smaller overhead as these start to grow.
const actions: Map<any, Function> = new Map([
	[SET_SELECTED_VIDEO, (state: any, action: Action) => (
		{ ...state, selectedVideo: action.payload })],
	[SET_SELECTED_AUDIO, (state: any, action: Action) => (
		{ ...state, selectedAudio: action.payload })],
	[SET_SELECTED_AUDIO_OUTPUT, (state: any, action: Action) => (
		{ ...state, selectedOutput: action.payload })],
	[SET_DISPLAY_NAME, (state: any, action: Action) => (
		{ ...state, displayName: action.payload })],
	[SET_CONNECTION_READY, (state: any, action: Action) => (
		{ ...state, connectionReady: action.payload })],
	[SET_MAIN_VIDEO_STREAM, (state: any, action: Action) => {
		const newState = { ...state };

		if (newState.mainVideoStream) {
			const oldTracks = newState.mainVideoStream.getTracks();
			oldTracks.forEach((track: MediaStreamTrack) => track.stop());
		}

		newState.mainVideoStream = action.payload;
		const localId = window?.localRtcId || '';
		newState.connectedPresenterVideos = newState.connectedPresenterVideos.map(
			(vid: VideoStream) => {
				const newVid = vid;
				if (newVid.self || newVid.id === localId) {
					newVid.srcObject = action.payload;
				}
				return newVid;
			},
		);

		return newState;
	}],
	[SET_SHARING_SCREEN, (state: any, action: Action) => (
		{ ...state, sharingScreen: action.payload })],
	[SET_CONNECTED_PRESENTER_VIDEOS, (state: any, action: Action) => (
		{ ...state, connectedPresenterVideos: action.payload })],
	[SET_CONNECTED_MEDIA_VIDEOS, (state: any, action: Action) => (
		{ ...state, connectedMediaVideos: action.payload })],
	[SET_VIDEO_DEVICES, (state: any, action: Action) => {
		const newState = { ...state };
		if (!newState.selectedVideo && newState.mainVideoStream) {
			const tracks = newState.mainVideoStream.getTracks();
			tracks.forEach((track: MediaStreamTrack) => track.stop());
			newState.mainVideoStream = null;
		}

		newState.videoDevices = action.payload;
		return newState;
	}],
	[SET_AUDIO_DEVICES, (state: any, action: Action) => {
		const newState = { ...state };

		if (!newState.selectedAudio && newState.mainVideoStream) {
			const tracks = newState.mainVideoStream.getTracks();
			tracks.forEach((track: MediaStreamTrack) => track.stop());
			newState.mainVideoStream = null;
		}

		newState.audioDevices = action.payload;
		return newState;
	}],
	[SET_AUDIO_OUTPUTS, (state: any, action: Action) => {
		const newState = { ...state };
		const newDevicesIncludesCurrent = action.payload.find(
			(device: any) => device.deviceId === newState.selectedOutput?.deviceId,
		);
		if (!newDevicesIncludesCurrent) {
			newState.selectedOutput = action.payload[0] ? action.payload[0] : null;
		}

		newState.audioOutputs = action.payload;
		return newState;
	}],
	[SET_SELECTED_RESOLUTION, (state: any, action: Action) => (
		{ ...state, selectedResolution: action.payload })],
	[SET_LIVE, (state: any, action: Action) => ({ ...state, isLive: action.payload })],
	[UPDATE_LOCAL_ID, (state: any, action: Action) => {
		const newState = { ...state };
		const localId = window?.localRtcId || '';
		newState.connectedPresenterVideos = newState.connectedPresenterVideos.map(
			(vid: VideoStream) => {
				const newVid = vid;
				if (newVid.id === 'local' || newVid.id === localId) {
					newVid.local = true;
					newVid.self = true;
					newVid.id = action.payload;
					if (!newVid.isMobile && newVid.srcObject !== newState.mainVideoStream) {
						newVid.srcObject = newState.mainVideoStream;
					}
				}
				return newVid;
			},
		);

		return newState;
	}],
	[SET_VIDEO_MUTE, (state: any, action: Action) => ({ ...state, videoMuted: action.payload })],
	[SET_AUDIO_MUTE, (state: any, action: Action) => ({ ...state, audioMuted: action.payload })],
	[SET_PREVIEW_MODE, (state: any, action: Action) => ({ ...state, previewMode: action.payload })],
	[SET_LIVE_VIDEO_MAP, (state: VideoState, action: Action) => (
		{ ...state, liveVideoMap: action.payload })],
	[DESTROY_MAIN_VIDEO_STREAM, (state: VideoState) => {
		if (state.mainVideoStream) {
			const tracks = state.mainVideoStream.getTracks();
			tracks.forEach((track: MediaStreamTrack) => {
				track.stop();
			});
		}
		return { ...state, mainVideoStream: null };
	}],
	[SET_HIDDEN_TITLES, (state: VideoState, action: Action) => (
		{ ...state, hiddenTitles: action.payload })],
	[SET_USE_MOBILE, (state: VideoState, action: Action) => (
		{ ...state, useMobile: action.payload })],
	[SYNC_VIDEO_REDUCER_STATE, (state: VideoState, action: Action) => (
		{ ...state, ...action.payload })],
	[ADD_PRESENTER_VIDEO, (state: VideoState, action: Action) => {
		let currentPresenterVideos = [...state.connectedPresenterVideos];
		const videoWhichExists = currentPresenterVideos.find(
			(vid) => (vid.profile === action.payload.profile)
			|| (vid.brandliveProfileId === action.payload.profile),
		);
		if (
			videoWhichExists
			&& !videoWhichExists.isScreenShare
			&& videoWhichExists.id === action.payload.id
			&& videoWhichExists.self !== true
		) {
			currentPresenterVideos = currentPresenterVideos.filter(
				(vid) => vid.profile !== action.payload.profile
				&& vid.brandliveProfileId !== action.payload.profile,
			);
		} else if (videoWhichExists && videoWhichExists.self === true) {
			console.log('duplicated user for self');
		} else if (videoWhichExists && action.payload.profile !== 0) { // 0 is screenshare it is ok
			console.log('duplicated user entered => ', action.payload.profile);
			return state;
		}
		currentPresenterVideos.push(action.payload);
		return { ...state, connectedPresenterVideos: currentPresenterVideos };
	}],
	[REMOVE_PRESENTER_VIDEO, (state: VideoState, action: Action) => (
		{
			...state,
			connectedPresenterVideos: state.connectedPresenterVideos.filter(
				(p) => p.id !== action.payload,
			),
		})],
	[RESET_AUDIO_MUTED_PREFS, (state: VideoState) => {
		const newState = { ...state };

		newState.connectedPresenterVideos = newState.connectedPresenterVideos.map(
			(vid: VideoStream) => {
				const newVid = vid;
				newVid.mutedPreference = false;
				newVid.userInitiated = false;

				return newVid;
			},
		);

		return newState;
	}],
	[SET_IS_INITIAL_DEVICE_LOAD, (state: VideoState, action: Action) => (
		{ ...state, isInitialDeviceLoad: action.payload }
	)],
	[ADD_FEED_WEBRTCID, (state: VideoState, action: Action) => {
		const newState = { ...state };
		if (!newState.feedWebRTCIds.includes(action.payload)) {
			newState.feedWebRTCIds = [...newState.feedWebRTCIds, action.payload];
		}
		return newState;
	}],
	[REMOVE_FEED_WEBRTCID, (state: VideoState, action: Action) => (
		{
			...state,
			feedWebRTCIds: state.feedWebRTCIds.filter(
				(p) => p !== action.payload,
			),
		}
	)],
	[CLEAR_FEED_WEBRTCIDS, (state: VideoState) => (
		{ ...state, feedWebRTCIds: [] }
	)],
	[SET_SHOULD_REPUBLISH_VIDEO, (state: any, action: Action) => (
		{ ...state, shouldRepublishVideo: action.payload }
	)],
	[GET_SRT_STREAMS, (state: VideoState, action: Action) => handle(state, action, {
		start: (s) => ({ ...s }),
		finish: (s) => ({ ...s }),
		failure: (s) => ({ ...s }),
		success: (s) => ({ ...s, srtStreams: action.payload }),
	})],
	[UPSERT_SRT_STREAM, (state: VideoState, action: Action) => {
		const upsertedStream = action.payload.entityValue.srtStream;
		if (!upsertedStream) return state;

		return {
			...state,
			srtStreams: arrayUpsert<SrtStream>(state.srtStreams, upsertedStream, 'id'),
		};
	}],
	[DELETE_SRT_STREAM, (state: VideoState, action: Action) => {
		const streamId = action.payload?.entityValue?.srtStream?.id;
		return {
			...state,
			srtStreams: state.srtStreams.filter((stream: SrtStream) => stream.id !== streamId),
			selectedSrtStreamId: state.selectedSrtStreamId === streamId ? 0 : state.selectedSrtStreamId,
		};
	}],
	[SET_SELECTED_SRT_STREAM_ID, (state: VideoState, action: Action) => (
		{ ...state, selectedSrtStreamId: action.payload }
	)],
	[GET_ONSITE_LOCATIONS, (state: VideoState, action: Action) => handle(state, action, {
		start: (s) => ({ ...s }),
		finish: (s) => ({ ...s }),
		failure: (s) => ({ ...s }),
		success: (s) => ({ ...s, onsiteLocations: action.payload, onsiteLocationsLoaded: true }),
	})],
	[UPSERT_ONSITE_LOCATION, (state: VideoState, action: Action) => {
		const upsertedLocation = action.payload.entityValue.onsiteLocation;
		if (!upsertedLocation) return state;

		return {
			...state,
			onsiteLocations: arrayUpsert<OnsiteLocation>(state.onsiteLocations, upsertedLocation, 'id'),
		};
	}],
	[DELETE_ONSITE_LOCATION, (state: VideoState, action: Action) => {
		const locationId = action.payload?.entityValue?.onsiteLocation?.id;
		return {
			...state,
			onsiteLocations: state.onsiteLocations.filter((l: OnsiteLocation) => l.id !== locationId),
			selectedOnsiteLocationId: state.selectedOnsiteLocationId === locationId
				? 0
				: state.selectedOnsiteLocationId,
		};
	}],
	[ADD_ONSITE_LOCATION_STREAM, (state: VideoState, action: Action) => {
		const { id, stream } = action.payload;
		const newStreams = new Map(state.onsiteLocationStreams);
		const existingLocationStreams = newStreams.get(id);
		if (existingLocationStreams) {
			newStreams.set(id, [...existingLocationStreams, stream]);
		} else {
			newStreams.set(id, [stream]);
		}
		return { ...state, onsiteLocationStreams: newStreams };
	}],
	[SET_ONSITE_LOCATION_STREAMS, (state: VideoState, action: Action) => {
		const { id, streams } = action.payload;
		const newStreams = new Map(state.onsiteLocationStreams);
		newStreams.set(id, streams);
		return { ...state, onsiteLocationStreams: newStreams };
	}],
	[REMOVE_ONSITE_LOCATION_STREAM, (state: VideoState, action: Action) => {
		const { id, webrtcId } = action.payload;
		const newStreams = new Map(state.onsiteLocationStreams);
		const existingLocationStreams = newStreams.get(id);
		if (existingLocationStreams) {
			const filteredStreams = existingLocationStreams.filter((s) => s.id !== webrtcId);
			if (filteredStreams.length) {
				newStreams.set(id, filteredStreams);
			} else {
				// remove the location if there are no streams left
				newStreams.delete(id);
			}
		}
		return { ...state, onsiteLocationStreams: newStreams };
	}],
	[RESET_ONSITE_LOCATION_STREAMS, (state: VideoState) => (
		{ ...state, onsiteLocationStreams: new Map() }
	)],
	[SET_ONSITE_LOCATION_SESSION, (state: VideoState, action: Action) => (
		{ ...state, onsiteLocationSession: action.payload }
	)],
	[SET_SELECTED_ONSITE_LOCATION_ID, (state: VideoState, action: Action) => (
		{ ...state, selectedOnsiteLocationId: action.payload }
	)],
	[UPSERT_ONSITE_LOCATION_LOCAL, (state: VideoState, action: Action) => {
		const upsertedLocation = action.payload;
		if (!upsertedLocation) return state;

		return {
			...state,
			onsiteLocations: arrayUpsert<OnsiteLocation>(state.onsiteLocations, upsertedLocation, 'id'),
		};
	}],
]);

export function VideoReducer(state: VideoState = initialVideoState, action: Action) {
	const newState = actions.get(action.type)?.(state, action) || state;

	if (action.type === SET_SELECTED_VIDEO && newState.selectedVideo?.deviceId) {
		localStorage.setItem('camera', newState.selectedVideo.deviceId);
	}

	if (action.type === SET_SELECTED_AUDIO) {
		localStorage.setItem('microphone', newState.selectedAudio?.deviceId ?? '');
	}

	if (action.type === SET_SELECTED_AUDIO_OUTPUT) {
		localStorage.setItem('speakers', newState.selectedOutput?.deviceId ?? '');
	}

	return newState;
}
